<template>

    <div class="mt-6">
        <form>
            <ErrorSummary :response-status="responseStatus" />
            <div class="mt-6">
                <div class="divide-y divide-gray-200">
                    <div class="gap-6 mt-6 md:grid md:grid-cols-4">
                        <div class="sm:col-span-4">
                            <label class="block text-sm font-medium text-gray-700" for="cover-photo">
                                Photos
                            </label>
                            <uc-config style="display: none" 
                                       pubkey="c1ecae68d0087aa3986a"
                                       ctx-name="my-uploader"
                                       img-only="true"
                                       multiple="false"
                                       :max-local-file-size-bytes = UploadCareSettings.maxLocalFileSizeBytes
                                       crop-preset="1:1"
                                       image-shrink="400x400"
                                       confirm-upload="true"
                                       remove-copyright="true"
                                       :source-list = UploadCareSettings.sourceList>
                            </uc-config>
                            <div
                                    class="flex justify-center px-6 pt-5 pb-6 mt-1 border-2 border-clay-500 border-dashed rounded-md">
                                <div class="space-y-1 text-center">

                                    <uc-upload-ctx-provider
                                            id="uploaderctx"
                                            ctx-name="my-uploader"
                                            @done-click="handleDoneEvent"
                                    ></uc-upload-ctx-provider>
                                    <uc-file-uploader-regular ctx-name="my-uploader"
                                                              class="uc-light">

                                        <p class="text-xs text-clay-800">
                                            PNG, JPG, GIF up to 4MB
                                        </p>
                                    </uc-file-uploader-regular>

                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            <div class="bg-white">
                <div class="py-8 mx-auto sm:py-12">
                    <div class="space-x-4">
                        <h2 class="text-lg font-medium text-gray-900">Your Headshots</h2>
                    </div>
                    <p v-if="headshots?.length < 1">
                        The world wants to see you! Please upload your headshot.
                    </p>
                    <div class="grid grid-cols-1 mt-6 gap-x-8 gap-y-8 sm:grid-cols-2 sm:gap-y-10 lg:grid-cols-4">
                        <div v-for="headshot in headshots" class="relative group">
                            <div
                                    class="overflow-hidden bg-gray-100 border rounded-lg shadow-lg aspect-square border-slate-900">
                                <img :src="headshot.fileName" class="object-cover object-center" alt="Headshot thumbnail" />
                            </div>
                            <div class="mt-3">
                                <div class="flex items-center justify-between">
                                    <span class="flex flex-col flex-grow">
                                        <span id="primaryHeadshot-label"
                                              class="text-sm font-medium text-gray-900">Primary</span>
                                    </span>
                                    <Switch v-model="headshot.isHeadshot"
                                            :class="[headshot.isHeadshot ? 'bg-powder-600' : 'bg-gray-200', 'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-powder-500']"
                                            v-on:click.prevent="makeHeadshotPrimary(headshot)">
                                        <span class="sr-only">Primary</span>
                                        <span
                                                :class="[headshot.isHeadshot ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transform ring-0 transition ease-in-out duration-200']"
                                                aria-hidden="true" />
                                    </Switch>
                                </div>
                            </div>
                            <div class="mt-3">
                                <SaButton @click.prevent="deleteHeadshot(headshot)" :loading="isDeleting[headshot.id]" variant="ghost">
                                    <template #loading>
                                        Deleting photo...
                                    </template>
                                    Delete photo
                                </SaButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>

    <NotificationSuccess :show="updateSuccessful" content="Your changes were successfully saved."
                         title="Successfully saved!" />

</template>

<script lang="ts" setup>
import {onMounted, Ref, ref} from "vue";
import {client, UploadCareSettings} from '@/shared';
import {
    AddProfileHeadshot,
    GetProfileHeadshots,
    GetProfileHeadshotsResponse,
    HeadshotDto,
    MakeProfileHeadshotPrimaryRequest,
    RemoveProfileHeadshotRequest,
} from "@/dtos";
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import NotificationSuccess from "@/main/components/NotificationSuccess.vue";
import {Switch} from '@headlessui/vue';
import {trackEvent} from '@/shared';
import * as UC from '@uploadcare/file-uploader';
import "@uploadcare/file-uploader/web/uc-file-uploader-inline.min.css"
import SaButton from "@/main/components/SaButton.vue";

UC.defineComponents(UC);

const responseStatus = ref(null);
const headshots: Ref<HeadshotDto[]> = ref([]);

const updateSuccessful: Ref<Boolean> = ref(false);

onMounted(async () => {
    await getHeadshots();
});

const getHeadshots = async () => {
    await client.get<GetProfileHeadshotsResponse>(new GetProfileHeadshots())
        .then(response => {
            headshots.value = response.headshots;
        });
}

const handleDoneEvent = async (e: any) => {
    if (e.detail && e.detail.status === "success") {
        const file = e.detail.successEntries[0];
        await onUploadSuccess(file);
        const uploaderCtx = document.querySelector('#uploaderctx')
        const api = uploaderCtx?.getAPI();
        api.removeAllFiles();
    }
}

const onUploadSuccess = async (response: any) => {
    await client.post<GetProfileHeadshotsResponse>(new AddProfileHeadshot({
        fileUrl: response.cdnUrl
    }))
        .then(async (res) => {
            updateSuccessful.value = true;
            responseStatus.value = null;
            headshots.value = res.headshots;

            trackEvent( 'profile_updated', {
                method: 'photo_added'
            });
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
}

const makeHeadshotPrimary = async (headshot: HeadshotDto) => {
    await client.post<GetProfileHeadshotsResponse>(new MakeProfileHeadshotPrimaryRequest({
        photoId: headshot.id
    }))
        .then(async (res) => {
            updateSuccessful.value = true;
            responseStatus.value = null;
            headshots.value = res.headshots;
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
}

const isDeleting = ref<Record<string, boolean>>({});
const deleteHeadshot = async (headshot: HeadshotDto) => {
    isDeleting.value[headshot.id] = true;

    await client.delete<GetProfileHeadshotsResponse>(new RemoveProfileHeadshotRequest({
        photoId: headshot.id
    }))
        .then(async (res) => {
            updateSuccessful.value = true;
            responseStatus.value = null;
            headshots.value = res.headshots;
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        })
        .finally(() => {
            isDeleting.value[headshot.id] = true; 
        });
}


</script>
<style>

</style>